import { useCallback, useMemo } from 'react'

import { MultiSelect, Select } from 'govwell-ui'
import { SelectBaseProps, SelectModeProps, SelectOption } from 'govwell-ui/components/Select/types'
import { DistanceUnit } from 'types/graphql'

const allDistanceUnitOptions: SelectOption<DistanceUnit>[] = [
  { label: 'Feet', value: 'Feet' },
  { label: 'Yards', value: 'Yards' },
  { label: 'Miles', value: 'Miles' },
]

type Props = {
  caption?: React.ReactNode
  isClearable?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  width?: React.CSSProperties['width']
  isDisabled?: boolean
  placeholder?: string
  filterOption?: SelectBaseProps<DistanceUnit>['filterOption']
} & SelectModeProps<DistanceUnit>

const DistanceUnitSelect = ({
  caption,
  isClearable,
  isRequired,
  label,
  onValueChange,
  value,
  width,
  isMultiSelect,
  isDisabled,
  placeholder = 'Select a unit',
  filterOption,
}: Props) => {
  const valueAsArray = useMemo(
    () => (Array.isArray(value) ? value : value == null ? [] : [value]),
    [value]
  )

  const selectedOptionSingleSelect = useMemo(() => {
    if (!isMultiSelect) {
      return allDistanceUnitOptions.find((o) => o.value === value)
    }
  }, [isMultiSelect, value])

  const selectedOptions = useMemo(
    () => allDistanceUnitOptions.filter((o) => valueAsArray.includes(o.value)),
    [valueAsArray]
  )

  const handleSelectedOptionsChange = useCallback(
    (_options: SelectOption<DistanceUnit>[]) => {
      if (isMultiSelect) {
        onValueChange(_options.map((o) => o.value))
      } else {
        onValueChange(_options[0]?.value ?? null)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<DistanceUnit> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(option?.value ?? null)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const getOptionKey = useCallback((option: SelectOption<DistanceUnit>) => option.value, [])

  const selectBaseProps: SelectBaseProps<DistanceUnit> = {
    caption,
    getOptionKey,
    isClearable,
    isRequired,
    label,
    options: allDistanceUnitOptions,
    placeholder,
    width,
    isDisabled,
    filterOption,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        {...selectBaseProps}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        selectedOptions={selectedOptions}
      />
    )
  }

  return (
    <Select
      {...selectBaseProps}
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOptionSingleSelect}
    />
  )
}

export default React.memo(DistanceUnitSelect)
